import {type Dispatch, type SetStateAction, useState} from 'react'

/**
 * Local state that resets to `initial` whenever the `trigger` boolean transitions
 * from `false` to `true`. Returns the reset value in the same render so consumers
 * see the cleared state without an extra commit cycle.
 *
 * Used in `RelationshipsSection` to clear three independent picker states when
 * the parent menu opens.
 *
 * `initial` is compared by reference. Callers must pass a stable value
 * (a module-level constant, `useMemo`'d object, or a primitive) — a fresh
 * object/array literal each render would not loop (the reset only fires on
 * trigger transition), but it would invalidate `===` comparisons that
 * consumers might do against the returned value.
 */
export function useResetOnEnter<T>(initial: T, trigger: boolean): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState(initial)
  const [prevTrigger, setPrevTrigger] = useState(trigger)
  if (trigger !== prevTrigger) {
    setPrevTrigger(trigger)
    if (trigger && value !== initial) {
      setValue(initial)
      return [initial, setValue]
    }
  }
  return [value, setValue]
}
